<template lang="pug">
.habit-confirmation-wrapper
  b-row(v-show="isLoading || isDownloading")
    b-col(cols="12")
      b-card.py-5
        b-card-body.text-center
          loading(
            :active="true"
            :is-full-page="false"
            color="#498ceb"
          )

  div(v-show="!isLoading && !isDownloading")
    b-row(v-if="!confirmation.process")
      h6.ml-2 {{ $t('message.process_not_found') }}

    div(v-else)
      b-row
        b-col(cols="12")
          b-card
            template(#header).d-flex.justify-content-between.align-items-start
              div
                h4.mb-1 {{ processName }}
                h6.text-muted.mb-1(v-if="confirmation.process.goal") {{ confirmation.process.goal }}
              div
                b-button(v-if="hasPermission('confirmations-daily', 'export')", variant="info", @click="downloadPDF()")
                  | {{ $t('DownloadPDF') }}
                b-button.ml-1(v-if="hasPermission('confirmations-daily', 'edit')", variant="warning", @click="reEvaluate(confirmation._id)")
                  | {{ $t('label.re-evaluate') }}

            b-card-text
              div(v-if="confirmation.process.description")
                | {{ confirmation.process.description }}
              div(v-if="confirmation.worker")
                | {{ $t('message.confirmation_for') }} {{ confirmation.worker.name }}
                span(v-if="confirmation.supervisor")
                  |  {{ $t('message.done_by') }} {{ confirmation.supervisor.name }}
              div(v-if="confirmation.duration")
                | {{ $t('message.duration') }}: {{ confirmation.duration }}
              b-row.mt-2
                b-col(cols="12")
                  b-card-group.p-0(deck, v-if="confirmation.total")
                    b-card(
                      bg-variant="transparent",
                      border-variant= "success",
                    )
                      b-card-body.p-0.text-center
                        b-card-title.text-success {{ activityScore(confirmation) }}
                        b-card-text
                          | {{ $t('message.tableHeader.cumplimiento') }}

                    b-card(
                      bg-variant="transparent",
                      border-variant= "info",
                    )
                      b-card-body.p-0.text-center
                        b-card-title.text-info {{ Number(confirmation.score) % 1 === 0 ? Number(confirmation.score) : Number(confirmation.score).toFixed(1) }}
                        b-card-text
                          | {{ $t('message.tableHeader.score') }}

                    b-card(
                      bg-variant="transparent",
                      border-variant= "warning",
                    )
                      b-card-body.p-0.text-center
                        b-card-title.text-warning {{ confirmation.total }}
                        b-card-text
                          | {{ $t('message.tableHeader.total') }}

              // Nuevo RR Calculado
              b-row.mt-2(v-if="newRRCalculation")
                b-col(cols="12")
                  b-card.mb-0(:title="$t('message.tableHeader.evaluationResult')")
                    b-card-text
                      b-row
                        b-col(cols="12", md="4")
                          div.mb-2
                            strong {{ $t('message.tableHeader.residualRiskPrevious') }}:
                            span.ml-2(:style="{ color: getRRColor(newRRCalculation.previousRR), 'font-weight': 'bold' }") {{ newRRCalculation.previousRR }}
                        b-col(cols="12", md="4")
                          div.mb-2
                            strong {{ $t('message.tableHeader.residualRiskNew') }}:
                            span.ml-2(:style="{ color: getRRColor(newRRCalculation.newRR), 'font-weight': 'bold' }") {{ newRRCalculation.newRR }}
                        b-col(cols="12", md="4")
                          div.mb-2
                            strong Nivel:
                            span.ml-2 {{ $t(`evaluationLevel.${newRRCalculation.level}`) }} ({{ newRRCalculation.compliancePercentage.toFixed(1) }}%)

      // Metadata
      b-row(v-if="confirmation.metadata && confirmation.metadata.length")
          b-col(cols="12")
            b-card(:title="$t('aditional_information')")
              b-card-text
                b-form-group(
                  v-for="(category, index) in confirmation.metadata", :key="index"
                  :label="$t(`metadata.${category.name}`)",
                  :label-for="$t(`metadata.${category.name}`)",
                  description=""
                )
                  v-select(
                    v-if="category.type === 'select'",
                    v-model="category.answer",
                    :disabled="true"
                  )
                  b-form-input(
                    v-if="category.type === 'input'",
                    v-model="category.answer",
                    :disabled="true"
                  )

      // Activities
      b-row(v-for="(activity, idx) in confirmation.activities", :key="idx")
        b-col(cols="12")
          b-card(:title="activity.name", :sub-title="activity.goal")
            b-card-text
              | {{ activity.description }}

            b-row
              b-col(cols="12")
                b-card-group.p-0(deck, v-if="activity.total")
                  b-card(
                    bg-variant="transparent",
                    border-variant= "success",
                    )
                    b-card-body.p-0.text-center
                      b-card-title.text-success {{ activityScore(activity) }}
                      b-card-text
                        | {{ $t('message.tableHeader.cumplimiento') }}

                  b-card(
                    bg-variant="transparent",
                    border-variant= "info",
                    )
                    b-card-body.p-0.text-center
                      b-card-title.text-info {{ Number(activity.score) % 1 === 0 ? Number(activity.score) : Number(activity.score).toFixed(1) }}
                      b-card-text
                        | {{ $t('message.tableHeader.score') }}

                  b-card(
                    bg-variant="transparent",
                    border-variant= "warning",
                    )
                    b-card-body.p-0.text-center
                      b-card-title.text-warning {{ activity.total }}
                      b-card-text
                        | {{ $t('message.tableHeader.total') }}
            
            b-row
              b-col(cols="12")
                b-button(
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'",
                  type="button",
                  :variant="activity.showBehaviours ? 'warning' : 'primary'",
                  class="mt-1 mb-1",
                  @click="activity.showBehaviours = !activity.showBehaviours"
                )
                  | {{ activity.showBehaviours ? $t('message.hide_behaviours') : $t('message.see_behaviours') }}

            b-row.mt-1(v-show="activity.showBehaviours")
              b-col(cols="4")
                b-form-group(:label="$t('label.populationSize')")
                  span {{ activity.population }}
              b-col(cols="4")
                b-form-group(:label="$t('label.sampleNumberToRequest')")
                  span {{ activity.sample }}
            
            hr
 
            b-row(v-for="(behaviour, index) in activity.behaviours" :key="index")
              b-col(v-show="activity.showBehaviours || behaviour.type === 'text'", cols="12")
                behaviour-question(
                  :behaviour="behaviour",
                  :activityName="activity.name",
                  :confirmationId="confirmation._id",
                  @isCreatingCase="handleLoading",
                  @reloadConfirmation="show"
                )

      // Image
      b-row(v-if="confirmation.imageKey")
        b-col(cols="12")
          b-card(:title="$t('message.confirmation_image')")
            b-card-text
              b-button.mr-1(
                v-ripple.400="'rgba(255, 255, 255, 0.15)'",
                type="button",
                variant="primary",
                @click="openImage(confirmation.imageKey)"
              )
                | {{ $t('message.open') }}

      // General Conclusion
      b-row(v-if="confirmation.generalConclusion")
        b-col(cols="12")
          b-card(:title="$t('message.generalConclusion')")
            b-card-text
              | {{ confirmation.generalConclusion }}

      // Add Multi-Behaviour Case
      b-row
        b-col(cols="12")
          b-card(:title="$t('add_improvement')")
            b-card-text
              b-button(
                variant="info"
                @click="createMultiBehaviourCase"
              )
                | {{ $t('message.add') }}
</template>

<script>
import { onMounted } from '@vue/composition-api/dist/vue-composition-api'
import { BRow, BCol, BCard, BCardText, BButton, BFormGroup, BFormSelect, BCardGroup, BCardBody, BCardTitle } from 'bootstrap-vue'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import BehaviourQuestion from './BehaviourQuestion.vue'
import useConfirmationView from './useConfirmationView'
import i18n from '@/libs/i18n'
import Loading from 'vue-loading-overlay'
import useCommon from "@/views/organization/useCommon";
import { hasPermission } from '@/composables/usePermission'

export default {
  components: {
    BRow,
    BCol,
    BCard,
    BCardText,
    BButton,
    BFormGroup,
    BFormSelect,
    BCardGroup,
    BCardBody,
    BCardTitle,
    BehaviourQuestion,
    vSelect,
    Loading,
  },

  directives: {
    Ripple,
  },

  computed: {
    processName() {
      return i18n.t('message.process') + `: ${this.confirmation?.process?.code ? `${this.confirmation.process.code} / ${this.confirmation?.process?.name || ''}` : this.confirmation?.process?.name || ''}`
    },
  },

  setup() {
    const { show, confirmation, activityScore, isLoading , reEvaluate, downloadPDF, isDownloading, createMultiBehaviourCase, newRRCalculation } = useConfirmationView()
    const { openImage } = useCommon()

    const handleLoading = (isCreatingCase) => {
      isLoading.value = isCreatingCase
    }

    const getRRColor = (rr) => {
      if (rr <= 5) return '#006400'
      if (rr <= 10) return '#2e8b57'
      if (rr <= 15) return '#d4a017'
      if (rr <= 20) return '#ff8c00'
      return '#cc0000'
    }

    onMounted(async () => {
      await show()
    })

    return {
      confirmation,
      activityScore,
      openImage,
      isLoading,
      show,
      handleLoading,
      reEvaluate,
      hasPermission,
      downloadPDF,
      isDownloading,
      createMultiBehaviourCase,
      newRRCalculation,
      getRRColor,
    }
  },
}
</script>

<style lang="scss">
.form-group {
  label {
    font-size: 1.2rem;
    font-weight: 200;
  }
}
</style>
